import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck } from 'react-icons/hi2';
import {
  Section,
  Container,
  Header,
  Label,
  Title,
  Subtitle,
  BillingToggle,
  ToggleButton,
  SavePill,
  Grid,
  Card,
  PopularBadge,
  PlanName,
  PlanTagline,
  PriceRow,
  Price,
  PricePeriod,
  PriceNote,
  FeatureList,
  FeatureItem,
  CheckIcon,
  CtaButton,
  Footnote,
} from './Pricing.styled';

// Self-serve signup is gated behind a build-time flag (default OFF).
// Vite env vars are strings, so compare against 'true'.
const SIGNUP_ENABLED = import.meta.env.VITE_SIGNUP_ENABLED === 'true';

// No app/signup URL is referenced elsewhere in the repo, so this is an
// assumed app origin for the live signup. Update if the app domain differs.
const SIGNUP_BASE_URL = 'https://app.scribia.pl/signup';

type Billing = 'monthly' | 'annual';

interface PlanConfig {
  id: 'solo' | 'kancelaria' | 'enterprise';
  signupParam: string | null; // null => no self-serve signup (always contact)
  highlighted: boolean;
}

const PLANS: PlanConfig[] = [
  { id: 'solo', signupParam: 'solo', highlighted: false },
  { id: 'kancelaria', signupParam: 'kancelaria', highlighted: true },
  { id: 'enterprise', signupParam: null, highlighted: false },
];

export default function Pricing() {
  const { t } = useTranslation();
  const [billing, setBilling] = useState<Billing>('monthly');

  return (
    <Section id="cennik">
      <Container>
        <Header>
          <Label>{t('pricing.label')}</Label>
          <Title>{t('pricing.title')}</Title>
          <Subtitle>{t('pricing.subtitle')}</Subtitle>
          <BillingToggle role="group" aria-label={t('pricing.label')}>
            <ToggleButton
              type="button"
              $active={billing === 'monthly'}
              aria-pressed={billing === 'monthly'}
              onClick={() => setBilling('monthly')}
            >
              {t('pricing.billing.monthly')}
            </ToggleButton>
            <ToggleButton
              type="button"
              $active={billing === 'annual'}
              aria-pressed={billing === 'annual'}
              onClick={() => setBilling('annual')}
            >
              {t('pricing.billing.annual')}
              <SavePill>−20%</SavePill>
            </ToggleButton>
          </BillingToggle>
        </Header>

        <Grid>
          {PLANS.map((plan) => {
            const base = `pricing.plans.${plan.id}`;
            const features = t(`${base}.features`, { returnObjects: true }) as string[];
            const isEnterprise = plan.id === 'enterprise';
            const isCustom = isEnterprise;

            // Enterprise is always "contact". Other plans: signup when the flag
            // is on, otherwise fall back to a contact action.
            const useSignup = SIGNUP_ENABLED && plan.signupParam !== null;
            const ctaLabel = useSignup ? t('pricing.ctaSignup') : t('pricing.ctaContact');
            const ctaHref = useSignup
              ? `${SIGNUP_BASE_URL}?plan=${plan.signupParam}`
              : '#faq';

            return (
              <Card key={plan.id} $highlighted={plan.highlighted}>
                {plan.highlighted && <PopularBadge>{t('pricing.badge.popular')}</PopularBadge>}
                <PlanName>{t(`${base}.name`)}</PlanName>
                <PlanTagline>{t(`${base}.tagline`)}</PlanTagline>

                {isCustom ? (
                  <>
                    <PriceRow>
                      <Price>{t(`${base}.priceCustom`)}</Price>
                    </PriceRow>
                    <PriceNote />
                  </>
                ) : (
                  <>
                    <PriceRow>
                      <Price>
                        {billing === 'monthly'
                          ? t(`${base}.priceMonthly`)
                          : t(`${base}.priceAnnual`)}
                      </Price>
                      <PricePeriod>{t('pricing.perMonth')}</PricePeriod>
                    </PriceRow>
                    <PriceNote>{billing === 'annual' ? t('pricing.annualNote') : ''}</PriceNote>
                  </>
                )}

                <FeatureList>
                  {features.map((feature) => (
                    <FeatureItem key={feature}>
                      <CheckIcon>
                        <HiCheck size={16} />
                      </CheckIcon>
                      {feature}
                    </FeatureItem>
                  ))}
                </FeatureList>

                <CtaButton href={ctaHref} $primary={plan.highlighted}>
                  {ctaLabel}
                </CtaButton>
              </Card>
            );
          })}
        </Grid>

        <Footnote>
          {t('pricing.footnote')}
          {SIGNUP_ENABLED ? ` ${t('pricing.trialNote')}` : ''}
        </Footnote>
      </Container>
    </Section>
  );
}
